import { RobotOutlined } from '@ant-design/icons';
import { Popover } from 'antd';
import { comparator, sort, toLower } from 'ramda';
import * as React from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { stylesheet } from 'typestyle';

import { AddButton, ListFixedHeader } from '@src/components';
import { DOMAIN } from '@src/dictionaries';
import { Alert } from '@src/controls';
import { FormattedMessage } from '@src/i18n';
import { merchantsActions } from '@src/modules/merchants';
import { routerActions } from '@src/modules/router';
import { services } from '@src/services';
import { MADError } from '@src/utils';
import { useDispatch } from '@src/utils/hooks';
import * as merchantService from '@src/services/merchant-service';
import { CreateForm, MerchantsList } from './components';

export const MerchantsListContainer: React.FunctionComponent = () => {
  const queryClient = useQueryClient();
  const {
    data: merchants = [],
    error,
    isLoading,
  } = useQuery<merchantService.ManagedMerchant[], MADError>(
    ['managed-merchants'],
    merchantService.listMerchantsWithSites
  );
  const [createPopoverVisible, setCreatePopoverVisible] = React.useState(false);

  const dispatch = useDispatch();

  const handleCreateMerchant = (name: string) => {
    setCreatePopoverVisible(false);
    services.selfCareService
      .createMerchant(name)
      .then(({ id }) => {
        services.notificationService.success(`Merchant '${name}' created`);
        dispatch(routerActions.push({ name: 'MERCHANT_DETAILS', detailsMerchantId: id }));
        dispatch(merchantsActions.listMerchants());
        queryClient.invalidateQueries(['managed-merchants']);
      })
      .catch((err: MADError) => {
        services.loggingService.logError(err);
        services.notificationService.error('Failed to create merchant', 'Error', 4);
        // A failed default-site setup does not delete the canonical merchant.
        queryClient.invalidateQueries(['managed-merchants']);
      });
  };

  const merchantNames = merchants.map(m => m.name).map(toLower);

  const sortedMerchants = sort(
    comparator((a, b) => toLower(a.name) < toLower(b.name)),
    merchants
  );

  return (
    <>
      <ListFixedHeader title={DOMAIN.MERCHANTS} IconComponent={RobotOutlined} showSearch={false} />
      <Popover
        destroyTooltipOnHide
        trigger="click"
        title={<FormattedMessage id="NEW_MERCHANT" />}
        visible={createPopoverVisible}
        onVisibleChange={setCreatePopoverVisible}
        content={<CreateForm onSubmit={handleCreateMerchant} forbidenNames={merchantNames} />}
      >
        <AddButton text={<FormattedMessage id="ADD_NEW" />} className={styles.addButton} />
      </Popover>
      {!!error && <Alert message="Failed to fetch merchant identities" type="error" />}
      <MerchantsList isFetching={isLoading} merchants={sortedMerchants} />
    </>
  );
};

const styles = stylesheet({
  addButton: {
    width: '100px',
    padding: 0,
    margin: '15px 0px',
  },
});
